import { FC, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { CommonCompanyProfile } from 'api/models/CommonCompanyProfile';
import { useGetCommonProfile, useListCommonProfiles } from 'services/hooks/companies';
import { CommonProfilePickerProps } from './CommonProfilePicker.props';

const CommonProfilePicker: FC<CommonProfilePickerProps> = ({selectedProfileId, onChange, disabled}) => {
  const [search, setSearch] = useState('');
  const [showSearch, setShowSearch] = useState(!selectedProfileId);

  const {data: selectedProfile, isLoading: profileLoading} = useGetCommonProfile(selectedProfileId);
  const {data: searchData, isLoading: searchLoading} = useListCommonProfiles(
    showSearch && search ? search : undefined,
  );

  useEffect(() => {
    if (selectedProfileId == null) {
      setShowSearch(true);
    }
  }, [selectedProfileId]);

  const searchResults: CommonCompanyProfile[] = (searchData?.results ?? searchData ?? []).filter(
    (p: CommonCompanyProfile) => p.id !== selectedProfileId,
  );

  const handleSelect = (profile: CommonCompanyProfile) => {
    onChange(profile.id!);
    setSearch('');
    setShowSearch(false);
  };

  const handleRemove = () => {
    onChange(null);
    setShowSearch(true);
  };

  return (
    <Box>
      <Divider sx={{my: 2}}/>
      <Typography variant="subtitle2" sx={{mb: 1}}>
                Common Profile
      </Typography>

      {profileLoading && <CircularProgress size={20}/>}

      {!profileLoading && selectedProfile && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
          <Chip
            label={selectedProfile.name}
            size="small"
            onDelete={handleRemove}
            disabled={disabled}
          />
          {!showSearch && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearch('');
                setShowSearch(true);
              }}
              disabled={disabled}
              sx={{minWidth: 0}}
            >
                            Change
            </Button>
          )}
        </Box>
      )}

      {!profileLoading && !selectedProfile && (
        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    No common profile linked.
        </Typography>
      )}

      {showSearch && (
        <>
          <TextField
            placeholder="Search profiles…"
            size="small"
            fullWidth
            value={search}
            onChange={e => setSearch(e.target.value)}
            disabled={disabled}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small"/>
                  </InputAdornment>
                ),
                endAdornment: searchLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16}/>
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {search.length > 0 && (
            <Paper variant="outlined" sx={{mt: 0.5, maxHeight: 200, overflow: 'auto'}}>
              {searchResults.length === 0 && !searchLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{p: 1.5}}>
                                    No profiles found.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {searchResults.map(profile => (
                    <ListItemButton key={profile.id} onClick={() => handleSelect(profile)}>
                      <ListItemText
                        primary={profile.name}
                        secondary={profile.cap_iq_id ?? undefined}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default CommonProfilePicker;
